import { Box, Flex, Text, VStack, useColorModeValue,
  HStack,
} from '@chakra-ui/react';
import { animate, motion, useMotionValue } from 'framer-motion';
import React, { useCallback } from 'react';
import { useUserStore } from 'store/user';

import useHasAccount from 'lib/hooks/useHasAccount';
import useNavItems, { isGroupItem } from 'lib/hooks/useNavItems';
import IconSvg from 'ui/shared/IconSvg';

import NavLinkIcon from '../NavLinkIcon';
import NavLink from '../vertical/NavLink';
import NavLinkGroup from './NavLinkGroup';

const DRAWER_WIDTH = 330;

interface Props {
  onNavLinkClick?: () => void;
  isMarketplaceAppPage?: boolean;
}

const NavigationMobile = ({ onNavLinkClick, isMarketplaceAppPage }: Props) => {
  const { mainNavItems, accountNavItems, userNavItems } = useNavItems();
  const { userRole } = useUserStore();
  const [ openedGroupIndex, setOpenedGroupIndex ] = React.useState(-1);

  const mainX = useMotionValue(0);
  const subX = useMotionValue(DRAWER_WIDTH);

  const onGroupItemOpen = (index: number) => () => {
    setOpenedGroupIndex(index);
    animate(mainX, -DRAWER_WIDTH, { ease: 'easeInOut' });
    animate(subX, 0, { ease: 'easeInOut' });
  };

  const onGroupItemClose = useCallback(() => {
    animate(mainX, 0, { ease: 'easeInOut' });
    animate(subX, DRAWER_WIDTH, {
      ease: 'easeInOut',
      onComplete: () => setOpenedGroupIndex(-1),
    });
  }, [ mainX, subX ]);

  const hasAccount = useHasAccount();

  const iconColor = useColorModeValue('blue.600', 'blue.300');

  const openedItem =
    userRole === 'ADMIN' ?
      mainNavItems[openedGroupIndex] :
      userNavItems[openedGroupIndex];

  const isCollapsed = isMarketplaceAppPage ? false : undefined;

  return (
    <Flex position="relative" flexDirection="column" flexGrow={ 1 }>
      <Box
        display="flex"
        flexDirection="column"
        flexGrow={ 1 }
        as={ motion.div }
        style={{ x: mainX }}
        maxHeight={ openedGroupIndex > -1 ? '100vh' : 'unset' }
        overflowY={ openedGroupIndex > -1 ? 'hidden' : 'unset' }
      >
        { userRole === 'ADMIN' && (
          <Box as="nav" mt={ 6 }>
            <VStack w="100%" as="ul" spacing="1" alignItems="flex-start">
              { userRole === 'ADMIN' &&
              mainNavItems.map((item, index) => {
                // @TODO:
                if (item.text === 'Charts & stats') {
                  return null;
                }
                if (isGroupItem(item)) {
                  return (
                    <NavLinkGroup
                      key={ item.text }
                      item={ item }
                      onClick={ onGroupItemOpen(index) }
                      isExpanded={ isMarketplaceAppPage }
                    />
                  );
                } else {
                  return (
                    <NavLink
                      key={ item.text }
                      item={ item }
                      onClick={ onNavLinkClick }
                      isCollapsed={ isCollapsed }
                      isSide={ true }
                    />
                  );
                }
              }) }
            </VStack>
          </Box>
        ) }

        { userRole === 'USER' && (
          <Box as="nav" mt={ 12 }>
            { userNavItems.map(item => {
              return (
                <>
                  <HStack
                    spacing={ 3 }
                    mb={ 3 }
                    overflow="hidden"
                    style={{ backgroundColor: 'blue.1200' }}
                  >
                    <Box ml={ 2 } display="flex">
                      <NavLinkIcon item={ item } color="white"/>
                    </Box>
                    <Text color="white" fontSize="md" fontWeight="semibold">
                      { item.text }
                    </Text>
                  </HStack>
                  <VStack spacing={ 1 } alignItems="start">
                    { item?.subItems?.map((subItem: any, index: number) =>
                      Array.isArray(subItem) ? (
                        <Box
                          key={ index }
                          w="100%"
                          as="ul"
                          _notLast={{
                            mb: 2,
                            pb: 2,
                            borderBottomWidth: '1px',
                            borderColor: 'divider',
                          }}
                        >
                          { subItem.map((subSubItem: any) => (
                            <NavLink
                              key={ subSubItem.text }
                              item={ subSubItem }
                              isCollapsed={ false }
                            />
                          )) }
                        </Box>
                      ) : <NavLink key={ subItem.text } item={ subItem } isCollapsed={ false }/>,
                    ) }
                  </VStack>
                </>
              );
            }) }</Box>
        ) }
        { hasAccount && (
          <Box
            as="nav"
            mt={ 3 }
            pt={ 3 }
            borderTopWidth="1px"
            borderColor="divider"
          >
            <VStack as="ul" spacing="1" alignItems="flex-start">
              { accountNavItems.map((item) => (
                <NavLink
                  key={ item.text }
                  item={ item }
                  onClick={ onNavLinkClick }
                  isCollapsed={ isCollapsed }
                />
              )) }
            </VStack>
          </Box>
        ) }
      </Box>
      { openedGroupIndex >= 0 && (
        <Box
          as={ motion.nav }
          w="100%"
          mt={ 6 }
          position="absolute"
          top={ 0 }
          style={{ x: subX }}
          key="sub"
        >
          <Flex
            alignItems="center"
            px={ 2 }
            py={ 2.5 }
            w="100%"
            h="50px"
            onClick={ onGroupItemClose }
            mb={ 1 }
          >
            <IconSvg
              name="arrows/east-mini"
              boxSize={ 6 }
              mr={ 2 }
              color="white"
            />
            <Text variant="secondary" fontSize="sm" color="white">
              { userRole === 'ADMIN' && mainNavItems[openedGroupIndex].text }
              { userRole === 'USER' && userNavItems[openedGroupIndex].text }
            </Text>
          </Flex>
          <Box w="100%" as="ul">
            { isGroupItem(openedItem) &&
              openedItem.subItems?.map((item, index) =>
                Array.isArray(item) ? (
                  <Box
                    key={ index }
                    w="100%"
                    as="ul"
                    _notLast={{
                      mb: 2,
                      pb: 2,
                      borderBottomWidth: '1px',
                      borderColor: 'divider',
                    }}
                  >
                    { item.map((subItem) => (
                      <NavLink
                        key={ subItem.text }
                        item={ subItem }
                        onClick={ onNavLinkClick }
                        isCollapsed={ isCollapsed }
                        isSide={ true }
                      />
                    )) }
                  </Box>
                ) : (
                  <NavLink
                    key={ item.text }
                    item={ item }
                    mb={ 1 }
                    onClick={ onNavLinkClick }
                    isCollapsed={ isCollapsed }
                    isSide={ true }
                  />
                ),
              ) }
          </Box>
        </Box>
      ) }
    </Flex>
  );
};

export default NavigationMobile;
